/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { 
  Calendar, Clock, Bell, Plus, Check, ChevronLeft, ChevronRight, AlertCircle, 
  Truck, Building2, FlaskConical, SearchCheck, MapPin, PackageCheck, ShieldAlert, 
  ArrowRight, User, X, Search, Sparkles, RefreshCw, Layers, CheckCircle2, CircleDashed
} from 'lucide-react';
import { CalendarEvent, Folio, ProcessStage, StageHistory } from '../types.ts';

interface LogisticsCalendarProps {
  events?: CalendarEvent[];
  folios?: Folio[];
  onAddEvent?: (event: CalendarEvent) => void;
  onUpdateFolio?: (updatedFolio: Folio) => void;
}

const EVENT_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  pickup: { bg: 'bg-indigo-50 border-indigo-200 text-indigo-700', text: 'text-indigo-700', label: 'Recolección' },
  delivery: { bg: 'bg-emerald-50 border-emerald-200 text-emerald-700', text: 'text-emerald-700', label: 'Entrega Planta' },
  billing: { bg: 'bg-amber-50 border-amber-200 text-amber-700', text: 'text-amber-700', label: 'Prefactura' },
  meet: { bg: 'bg-blue-50 border-blue-200 text-blue-700', text: 'text-blue-700', label: 'Sala Meet' }
};

const STAGE_CONFIG: Record<string, { label: string; shortLabel: string; bg: string; border: string; text: string; icon: React.ElementType }> = {
  RECOLECCION: {
    label: 'Recolección de Carga',
    shortLabel: 'Recolección',
    bg: 'bg-indigo-50',
    border: 'border-indigo-300',
    text: 'text-indigo-700',
    icon: Truck
  },
  ENTRADA_CEDIS: {
    label: 'Ingreso a CEDIS',
    shortLabel: 'Ingreso CEDIS',
    bg: 'bg-blue-50',
    border: 'border-blue-300',
    text: 'text-blue-700',
    icon: Building2
  },
  DECAPADO_TERMICO_QUIMICO: {
    label: 'Decapado Térmico/Químico',
    shortLabel: 'Decapado Quím/Térm',
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-800',
    icon: FlaskConical
  },
  DECAPADO_LAVADO: {
    label: 'Decapado y Lavado',
    shortLabel: 'Decapado Lavado',
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-800',
    icon: FlaskConical
  },
  INSPECCION: {
    label: 'Inspección de Calidad',
    shortLabel: 'Inspección',
    bg: 'bg-cyan-50',
    border: 'border-cyan-300',
    text: 'text-cyan-800',
    icon: SearchCheck
  },
  RETRABAJO: {
    label: 'En Retrabajo',
    shortLabel: 'Retrabajo',
    bg: 'bg-rose-50',
    border: 'border-rose-300',
    text: 'text-rose-700',
    icon: ShieldAlert
  },
  SALIDA_CEDIS: {
    label: 'Salida de CEDIS / En Tránsito',
    shortLabel: 'Salida CEDIS',
    bg: 'bg-sky-50',
    border: 'border-sky-300',
    text: 'text-sky-800',
    icon: MapPin
  },
  ENTREGA_PLANTA: {
    label: 'Entrega en Planta / Cliente',
    shortLabel: 'Entrega Planta',
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    text: 'text-emerald-800',
    icon: PackageCheck
  },
  DEVOLUCION: {
    label: 'Devolución Rechazada',
    shortLabel: 'Devolución',
    bg: 'bg-red-100',
    border: 'border-red-400',
    text: 'text-red-800',
    icon: AlertCircle
  }
};

// Standard operational pipeline order for timeline view
const CORE_TIMELINE_PHASES: { stageKey: ProcessStage; title: string; subtitle: string; icon: React.ElementType }[] = [
  { stageKey: 'RECOLECCION', title: 'Recolección', subtitle: 'Embarque y recolección con chofer asignado', icon: Truck },
  { stageKey: 'ENTRADA_CEDIS', title: 'Ingreso al CEDIS', subtitle: 'Llegada y recepción en patio/almacén', icon: Building2 },
  { stageKey: 'DECAPADO_TERMICO_QUIMICO', title: 'Decapado Químico/Térmico', subtitle: 'Proceso de tina ácida y horno térmico', icon: FlaskConical },
  { stageKey: 'INSPECCION', title: 'Inspección de Calidad', subtitle: 'Verificación física y dictamen técnico', icon: SearchCheck },
  { stageKey: 'SALIDA_CEDIS', title: 'Salida de CEDIS', subtitle: 'Despacho de carga y tránsito a destino', icon: MapPin },
  { stageKey: 'ENTREGA_PLANTA', title: 'Entrega en Planta', subtitle: 'Recepción y firma de conformidad final', icon: PackageCheck }
];

function getStageIndex(stage: ProcessStage): number {
  switch (stage) {
    case 'RECOLECCION': return 0;
    case 'ENTRADA_CEDIS': return 1;
    case 'DECAPADO_TERMICO_QUIMICO':
    case 'DECAPADO_LAVADO': return 2;
    case 'INSPECCION':
    case 'RETRABAJO': return 3;
    case 'SALIDA_CEDIS': return 4;
    case 'ENTREGA_PLANTA':
    case 'DEVOLUCION': return 5;
    default: return 0;
  }
}

export default function LogisticsCalendar({
  events = [],
  folios = [],
  onAddEvent,
  onUpdateFolio
}: LogisticsCalendarProps) {
  // Current year/month (Default July 2026 for system session)
  const [currentYear, setCurrentYear] = useState(2026);
  const [currentMonth, setCurrentMonth] = useState(6); // 0-indexed, so 6 is July

  const [showAddReminder, setShowAddReminder] = useState(false);
  const [remDate, setRemDate] = useState('2026-07-14');
  const [remTitle, setRemTitle] = useState('');
  const [remType, setRemType] = useState<'pickup' | 'delivery' | 'billing' | 'meet'>('pickup');
  const [remDesc, setRemDesc] = useState('');

  // Selected Folio for Timeline modal
  const [selectedFolioTimeline, setSelectedFolioTimeline] = useState<Folio | null>(null);

  // Search filter
  const [searchTerm, setSearchTerm] = useState('');

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Days in month
  const daysInMonth = useMemo(() => {
    return new Date(currentYear, currentMonth + 1, 0).getDate();
  }, [currentYear, currentMonth]);

  const firstDayIndex = useMemo(() => {
    return new Date(currentYear, currentMonth, 1).getDay();
  }, [currentYear, currentMonth]);

  // Generate calendar days grid
  const calendarCells = useMemo(() => {
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDayIndex; i++) {
      cells.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push(i);
    }
    return cells;
  }, [daysInMonth, firstDayIndex]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Helper to format day matching string (YYYY-MM-DD)
  const getDayString = (day: number) => {
    const mm = String(currentMonth + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${currentYear}-${mm}-${dd}`;
  };

  // Valid existing folio IDs set for fast checking
  const activeFolioIdsSet = useMemo(() => {
    return new Set((folios || []).map(f => f.id));
  }, [folios]);

  // Clean custom events: Filter out any event referencing a folio that no longer exists in `folios`
  const validEvents = useMemo(() => {
    return (events || []).filter(e => {
      if (e.refId) {
        // If refId starts with folio or resembles folio ID, check if it exists in folios
        const looksLikeFolio = activeFolioIdsSet.has(e.refId) || (folios || []).some(f => e.refId === f.id);
        if (e.title.toLowerCase().includes('folio') && !looksLikeFolio) {
          return false;
        }
      }
      // Check if title mentions a folio that was deleted
      const match = e.title.match(/Folio\s+([\w\d\-\s]+)/i);
      if (match && match[1]) {
        const folioIdMentioned = match[1].trim();
        const exists = (folios || []).some(f => f.id === folioIdMentioned || f.id.includes(folioIdMentioned));
        if (!exists) return false;
      }
      return true;
    });
  }, [events, folios, activeFolioIdsSet]);

  // Filter active folios based on search term
  const filteredFolios = useMemo(() => {
    if (!searchTerm) return folios || [];
    const term = searchTerm.toLowerCase();
    return (folios || []).filter(f => 
      f.id.toLowerCase().includes(term) ||
      f.clientName.toLowerCase().includes(term) ||
      (f.costCenterName && f.costCenterName.toLowerCase().includes(term)) ||
      (f.driverName && f.driverName.toLowerCase().includes(term)) ||
      (f.destination && f.destination.toLowerCase().includes(term))
    );
  }, [folios, searchTerm]);

  // Find folios and events for specific day
  const getDayItems = (day: number) => {
    const dateStr = getDayString(day);

    // 1. Folios scheduled or created on this date
    const dayFolios = (filteredFolios || []).filter(f => {
      if (f.shipmentDate === dateStr) return true;
      if (f.createdDate && f.createdDate.substring(0, 10) === dateStr) return true;
      // Also check stage history dates
      if (f.history && f.history.some(h => h.timestamp && h.timestamp.substring(0, 10) === dateStr)) return true;
      return false;
    });

    // 2. Custom events on this date
    const dayEvents = validEvents.filter(e => e.date === dateStr);

    return { dayFolios, dayEvents };
  };

  // Add Reminder submit
  const handleAddReminderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!remTitle || !remDate) return;

    const newEvent: CalendarEvent = {
      id: `ev-${Math.random().toString(36).substr(2, 9)}`,
      title: remTitle.trim(),
      description: remDesc.trim(),
      date: remDate,
      type: remType
    };

    if (onAddEvent) {
      onAddEvent(newEvent);
    }
    
    // Reset Form
    setRemTitle('');
    setRemDesc('');
    setShowAddReminder(false);
    if (window.showAppAlert) {
      await window.showAppAlert('Recordatorio calendarizado correctamente.', 'Recordatorio Programado');
    }
  };

  return (
    <div className="space-y-6" id="logistics-calendar-workspace">
      
      {/* TOP HEADER & SEARCH BAR */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="space-y-1 text-center md:text-left">
          <div className="flex items-center gap-2 justify-center md:justify-start">
            <Calendar className="h-5 w-5 text-brand-red" />
            <h3 className="text-base font-black text-slate-900 font-display tracking-tight uppercase">
              Calendario Operativo y Línea del Tiempo
            </h3>
          </div>
          <p className="text-xs text-slate-500 font-medium">
            Rastreo de folios activos, programación de recolecciones y cronograma de fases de servicio.
          </p>
        </div>

        {/* Search input for Folios */}
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative w-full md:w-72">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por N° Folio, Cliente o Chofer..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs focus:outline-none focus:border-brand-red focus:bg-white text-slate-800 transition-all"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* CALENDAR MONTH GRID (COL-SPAN-2) */}
        <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          
          {/* Month Navigation */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="space-y-0.5">
              <h4 className="text-base font-black text-slate-900 tracking-tight font-display uppercase">
                {monthNames[currentMonth]} {currentYear}
              </h4>
              <p className="text-xs text-slate-400 font-medium">
                Sincronizado en tiempo real con el panel de folios
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button 
                onClick={handlePrevMonth} 
                className="p-1.5 hover:bg-slate-100 border border-slate-200 rounded-lg text-slate-600 transition-all cursor-pointer"
                title="Mes Anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button 
                onClick={handleNextMonth} 
                className="p-1.5 hover:bg-slate-100 border border-slate-200 rounded-lg text-slate-600 transition-all cursor-pointer"
                title="Mes Siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div>
            {/* Weekdays */}
            <div className="grid grid-cols-7 gap-1 text-center font-bold text-slate-400 text-[10px] uppercase tracking-wider pb-2 font-mono">
              <span>Dom</span>
              <span>Lun</span>
              <span>Mar</span>
              <span>Mié</span>
              <span>Jue</span>
              <span>Vie</span>
              <span>Sáb</span>
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1.5">
              {calendarCells.map((day, idx) => {
                if (day === null) {
                  return <div key={`empty-${idx}`} className="h-28 bg-slate-50/40 rounded-xl border border-slate-100/50"></div>;
                }

                const { dayFolios, dayEvents } = getDayItems(day);
                const isToday = currentYear === 2026 && currentMonth === 6 && day === 14;

                return (
                  <div 
                    key={`day-${day}`} 
                    className={`h-28 p-1.5 rounded-xl border flex flex-col justify-between overflow-hidden transition-all ${
                      isToday 
                        ? 'border-brand-red bg-red-50/10 shadow-xs ring-1 ring-brand-red/30' 
                        : 'border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-xs'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] font-black ${
                        isToday ? 'bg-brand-red text-white px-1.5 py-0.5 rounded-md font-mono' : 'text-slate-400'
                      }`}>
                        {day}
                      </span>
                      {dayFolios.length > 0 && (
                        <span className="text-[8px] font-black bg-slate-900 text-white px-1 py-0.2 rounded-xs font-mono">
                          {dayFolios.length} {dayFolios.length === 1 ? 'FOLIO' : 'FOLIOS'}
                        </span>
                      )}
                    </div>

                    {/* Day Events & Folios stack */}
                    <div className="space-y-1 overflow-y-auto max-h-20 pr-0.5 pt-1">
                      
                      {/* Active Folios */}
                      {dayFolios.map(folio => {
                        const stageInfo = STAGE_CONFIG[folio.status] || STAGE_CONFIG['RECOLECCION'];
                        return (
                          <div 
                            key={`f-${folio.id}`} 
                            onClick={() => setSelectedFolioTimeline(folio)}
                            className={`p-1 rounded-md border text-[9px] font-bold leading-tight cursor-pointer hover:scale-[1.02] transition-all ${stageInfo.bg} ${stageInfo.border} ${stageInfo.text} shadow-2xs`}
                            title={`Folio ${folio.id} - ${folio.clientName} (${stageInfo.label}). Clic para ver línea del tiempo.`}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="font-mono font-black truncate">{folio.id}</span>
                              <span className="text-[7px] uppercase font-bold px-1 rounded-xs bg-white/70 border border-slate-300 shrink-0">
                                {stageInfo.shortLabel}
                              </span>
                            </div>
                            <p className="text-[8px] font-medium text-slate-600 truncate mt-0.5">
                              {folio.clientName}
                            </p>
                          </div>
                        );
                      })}

                      {/* Custom Events */}
                      {dayEvents.map(ev => {
                        const style = EVENT_STYLES[ev.type] || { bg: 'bg-slate-100 border-slate-200', text: 'text-slate-700' };
                        return (
                          <div 
                            key={ev.id} 
                            className={`px-1 py-0.5 rounded-sm border text-[8px] font-bold leading-tight truncate ${style.bg} ${style.text}`}
                            title={`${ev.title}: ${ev.description}`}
                          >
                            📌 {ev.title}
                          </div>
                        );
                      })}

                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* SIDEBAR: FOLIOS REGISTRADOS Y TIMELINE DIRECTO */}
        <div className="space-y-6">
          
          {/* LISTA DE FOLIOS ACTIVOS / BÚSQUEDA */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
              <div className="flex items-center gap-2">
                <Layers className="h-4.5 w-4.5 text-slate-700" />
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-tight font-display">
                  Folios en Operación ({filteredFolios.length})
                </h4>
              </div>
              <span className="text-[9px] font-bold text-slate-400 font-mono bg-slate-100 px-2 py-0.5 rounded-full">
                Sincronizado
              </span>
            </div>

            <p className="text-[10px] text-slate-500 font-medium">
              Seleccione cualquier folio para desplegar la **Línea del Tiempo** interactiva con sus 6 fases operativas.
            </p>

            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
              {filteredFolios.length === 0 ? (
                <div className="p-6 text-center text-slate-400 space-y-2 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <Truck className="h-6 w-6 mx-auto opacity-30 text-slate-400" />
                  <p className="text-xs font-bold text-slate-500">No hay folios activos coincidentes.</p>
                  <p className="text-[10px]">Los folios eliminados en "Mis Cargas" no se muestran aquí.</p>
                </div>
              ) : (
                filteredFolios.map((folio) => {
                  const stageInfo = STAGE_CONFIG[folio.status] || STAGE_CONFIG['RECOLECCION'];
                  const StageIcon = stageInfo.icon;

                  return (
                    <div 
                      key={folio.id}
                      onClick={() => setSelectedFolioTimeline(folio)}
                      className={`p-3 rounded-xl border transition-all cursor-pointer hover:shadow-xs space-y-2 ${
                        selectedFolioTimeline?.id === folio.id 
                          ? 'bg-slate-900 text-white border-slate-900 ring-2 ring-brand-red/50' 
                          : 'bg-slate-50/70 border-slate-200/80 hover:bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <span className={`p-1 rounded-md text-xs ${
                            selectedFolioTimeline?.id === folio.id ? 'bg-slate-800 text-brand-red' : `${stageInfo.bg} ${stageInfo.text}`
                          }`}>
                            <StageIcon className="h-3.5 w-3.5" />
                          </span>
                          <span className="text-xs font-black font-mono tracking-tight">{folio.id}</span>
                        </div>
                        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-sm uppercase ${
                          selectedFolioTimeline?.id === folio.id 
                            ? 'bg-slate-800 text-amber-300 border border-slate-700' 
                            : `${stageInfo.bg} ${stageInfo.text} ${stageInfo.border}`
                        }`}>
                          {stageInfo.shortLabel}
                        </span>
                      </div>

                      <div className="text-[10px] space-y-0.5">
                        <p className={`font-bold truncate ${selectedFolioTimeline?.id === folio.id ? 'text-white' : 'text-slate-800'}`}>
                          {folio.clientName}
                        </p>
                        {folio.costCenterName && (
                          <p className={`text-[9px] truncate ${selectedFolioTimeline?.id === folio.id ? 'text-slate-400' : 'text-slate-500'}`}>
                            🏭 {folio.costCenterName}
                          </p>
                        )}
                      </div>

                      <div className={`flex items-center justify-between text-[9px] pt-1 border-t font-mono ${
                        selectedFolioTimeline?.id === folio.id ? 'border-slate-800 text-slate-400' : 'border-slate-100 text-slate-500'
                      }`}>
                        <span>🗓️ {folio.shipmentDate || folio.createdDate?.substring(0, 10)}</span>
                        <span className="text-brand-red font-bold flex items-center gap-1">
                          Ver Timeline <ArrowRight className="h-2.5 w-2.5" />
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ADD RECORDATORIO SECTION */}
          {showAddReminder ? (
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <h4 className="text-xs font-bold text-slate-900 flex items-center gap-1.5 uppercase font-display">
                  <Bell className="h-4 w-4 text-brand-red" /> Crear Recordatorio Manual
                </h4>
                <button onClick={() => setShowAddReminder(false)} className="text-slate-400 text-xs hover:text-slate-600">Cancelar</button>
              </div>

              <form onSubmit={handleAddReminderSubmit} className="space-y-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase">Título / Tarea</label>
                  <input
                    type="text"
                    required
                    placeholder="Recordatorio: Salida de camión..."
                    value={remTitle}
                    onChange={(e) => setRemTitle(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-brand-red"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase">Fecha del Recordatorio</label>
                  <input
                    type="date"
                    required
                    value={remDate}
                    onChange={(e) => setRemDate(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-brand-red"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase">Tipo de Evento</label>
                  <select
                    value={remType}
                    onChange={(e: any) => setRemType(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-brand-red"
                  >
                    <option value="pickup">Recolección (Logística)</option>
                    <option value="delivery">Entrega Planta (Logística)</option>
                    <option value="billing">Cobro / Prefactura</option>
                    <option value="meet">Reunión / Auditoría</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase">Notas Adicionales</label>
                  <input
                    type="text"
                    placeholder="Detalles para el operador..."
                    value={remDesc}
                    onChange={(e) => setRemDesc(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-brand-red"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-black text-white py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 cursor-pointer"
                >
                  <Check className="h-4 w-4 text-emerald-400" /> Guardar Recordatorio
                </button>
              </form>
            </div>
          ) : (
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-bold text-slate-700">Recordatorios Operativos</span>
              </div>
              <button
                onClick={() => setShowAddReminder(true)}
                className="text-xs text-brand-red font-bold flex items-center gap-1 hover:underline cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" /> Agregar
              </button>
            </div>
          )}

        </div>

      </div>

      {/* MODAL DETALLE: LÍNEA DEL TIEMPO COMPLETA DEL FOLIO */}
      {selectedFolioTimeline && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
          <div className="bg-white w-full max-w-3xl rounded-2xl border border-slate-200 shadow-2xl overflow-hidden my-8 space-y-0">
            
            {/* Modal Header */}
            <div className="bg-slate-900 text-white p-5 flex items-center justify-between border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-brand-red text-white rounded-xl">
                  <Truck className="h-6 w-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-amber-400 uppercase tracking-widest bg-amber-950/60 border border-amber-800/80 px-2 py-0.5 rounded-md">
                      FOLIO: {selectedFolioTimeline.id}
                    </span>
                    {STAGE_CONFIG[selectedFolioTimeline.status] && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${STAGE_CONFIG[selectedFolioTimeline.status].bg} ${STAGE_CONFIG[selectedFolioTimeline.status].text}`}>
                        {STAGE_CONFIG[selectedFolioTimeline.status].label}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-black text-white font-display mt-0.5">
                    {selectedFolioTimeline.clientName}
                  </h3>
                  <p className="text-xs text-slate-400 font-medium">
                    {selectedFolioTimeline.costCenterName || 'Centro de Distribución'}
                  </p>
                </div>
              </div>

              <button 
                onClick={() => setSelectedFolioTimeline(null)}
                className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl transition-all cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
              
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200/80 text-xs font-mono">
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase block font-sans">Origen:</span>
                  <span className="font-bold text-slate-800 truncate block">{selectedFolioTimeline.origin || 'Planta Cliente'}</span>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase block font-sans">Destino:</span>
                  <span className="font-bold text-slate-800 truncate block">{selectedFolioTimeline.destination || 'CEDIS Principal'}</span>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase block font-sans">Chofer:</span>
                  <span className="font-bold text-slate-800 truncate block">{selectedFolioTimeline.driverName || 'Sin asignar'}</span>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase block font-sans">Fecha de Envío:</span>
                  <span className="font-bold text-brand-red block">{selectedFolioTimeline.shipmentDate || selectedFolioTimeline.createdDate?.substring(0, 10)}</span>
                </div>
              </div>

              {/* LÍNEA DEL TIEMPO INTERACTIVA (TIMELINE) */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <h4 className="text-xs font-black uppercase text-slate-900 tracking-wider font-display flex items-center gap-2">
                    <Clock className="h-4 w-4 text-brand-red" />
                    Línea del Tiempo y Historial de Fases
                  </h4>
                  <span className="text-[10px] text-slate-400 font-medium">
                    Trazabilidad en Vivo
                  </span>
                </div>

                {/* Timeline Step Cards */}
                <div className="relative pl-6 space-y-6 before:content-[''] before:absolute before:left-3 before:top-3 before:bottom-3 before:w-0.5 before:bg-slate-200">
                  {CORE_TIMELINE_PHASES.map((phase, idx) => {
                    const currentStageIdx = getStageIndex(selectedFolioTimeline.status);
                    const phaseStageIdx = getStageIndex(phase.stageKey);

                    // Check if this phase exists in history
                    const historyRecord = (selectedFolioTimeline.history || []).find(h => {
                      if (h.stage === phase.stageKey) return true;
                      if (phase.stageKey === 'DECAPADO_TERMICO_QUIMICO' && (h.stage === 'DECAPADO_TERMICO_QUIMICO' || h.stage === 'DECAPADO_LAVADO')) return true;
                      return false;
                    });

                    const isCompleted = phaseStageIdx < currentStageIdx || !!historyRecord;
                    const isCurrent = phaseStageIdx === currentStageIdx && !isCompleted;
                    const isPending = phaseStageIdx > currentStageIdx && !historyRecord;

                    const IconComp = phase.icon;

                    return (
                      <div key={phase.stageKey} className="relative flex items-start gap-4">
                        
                        {/* Step Marker Node */}
                        <div className={`absolute -left-6 top-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                          isCompleted 
                            ? 'bg-emerald-500 text-white shadow-xs ring-4 ring-emerald-100' 
                            : isCurrent 
                            ? 'bg-brand-red text-white ring-4 ring-red-100 animate-pulse' 
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          {isCompleted ? <Check className="h-3.5 w-3.5" /> : (idx + 1)}
                        </div>

                        {/* Phase Details Card */}
                        <div className={`flex-1 p-4 rounded-xl border transition-all ${
                          isCurrent 
                            ? 'bg-red-50/20 border-red-300 ring-1 ring-red-200' 
                            : isCompleted 
                            ? 'bg-emerald-50/20 border-emerald-200/80' 
                            : 'bg-slate-50 border-slate-200/60 opacity-70'
                        }`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                            <div className="flex items-center gap-2">
                              <span className={`p-1.5 rounded-lg ${
                                isCompleted ? 'bg-emerald-100 text-emerald-700' : isCurrent ? 'bg-red-100 text-brand-red' : 'bg-slate-200 text-slate-500'
                              }`}>
                                <IconComp className="h-4 w-4" />
                              </span>
                              <div>
                                <h5 className="text-xs font-black text-slate-900 uppercase font-display">
                                  {phase.title}
                                </h5>
                                <p className="text-[10px] text-slate-500 font-medium">{phase.subtitle}</p>
                              </div>
                            </div>

                            <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full w-fit ${
                              isCompleted 
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' 
                                : isCurrent 
                                ? 'bg-red-100 text-brand-red border border-red-300 animate-pulse' 
                                : 'bg-slate-200 text-slate-600'
                            }`}>
                              {isCompleted ? '✓ COMPLETADO' : isCurrent ? '⏳ EN PROCESO ACTUAL' : 'PENDIENTE'}
                            </span>
                          </div>

                          {/* Historical Record details if present */}
                          {historyRecord && (
                            <div className="mt-2 pt-2 border-t border-slate-200/60 text-[10px] font-mono text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                              <span>⏱️ Registro: <strong>{new Date(historyRecord.timestamp).toLocaleString('es-MX')}</strong></span>
                              <span>👤 Operador: <strong>{historyRecord.operatorName || 'Sistema'}</strong></span>
                              {historyRecord.notes && <span>📝 {historyRecord.notes}</span>}
                            </div>
                          )}
                        </div>

                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Items List in Folio */}
              {selectedFolioTimeline.items && selectedFolioTimeline.items.length > 0 && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 space-y-2">
                  <h5 className="text-xs font-bold text-slate-800 uppercase font-display">
                    Mercancía y Piezas en este Folio:
                  </h5>
                  <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                    {selectedFolioTimeline.items.map((it, idx) => (
                      <div key={idx} className="bg-white p-2.5 rounded-lg border border-slate-200/80 flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-800">{it.description}</span>
                        <span className="font-mono font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded-md">
                          Cant: {it.quantity}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Modal Footer */}
            <div className="bg-slate-100 p-4 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setSelectedFolioTimeline(null)}
                className="bg-slate-900 hover:bg-black text-white px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                Cerrar Línea del Tiempo
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
